// New since you looked (release 6.3): the blue dots. The server lists what other people added that you have not
// looked at (GET /seen, per activity and per new track); the trail is worked out here, so every level agrees:
//   sidebar space row  <- anything new in the space (a new track, or an activity row with a dot);
//   track card         <- the track is new, or an activity in it has a dot;
//   activity row       <- the activity is new, or has updates, minutes or files since you opened it;
//   activity tab       <- Updates, Minutes or Files has something since you opened that tab.
// Looking clears at once on screen (withSeen), then on the server (POST /seen), the same on every device.
import type { NewOnActivity, NewSince, SeenKind } from '@dpw/shared';

export const EMPTY_NEW: NewSince = { activities: {}, tracks: {}, kb: false, research: false };

export interface Dots {
  space: (id: string) => boolean;
  track: (id: string) => boolean;
  /** The track itself is new (made by someone else); opening it clears that, not what is new inside it. */
  trackIsNew: (id: string) => boolean;
  /** The activity's row (and a sub-activity's row in its parent). */
  row: (id: string) => boolean;
  /** The activity's tabs. */
  tabs: (id: string) => Pick<NewOnActivity, 'updates' | 'minutes' | 'files'>;
  kb: boolean; research: boolean;
}

export function dotsOf(n: NewSince | undefined): Dots {
  const d = n ?? EMPTY_NEW;
  const spaces = new Set<string>(), tracks = new Set<string>();
  for (const [id, t] of Object.entries(d.tracks)) { tracks.add(id); spaces.add(t.spaceId); }
  for (const a of Object.values(d.activities)) if (a.row) { tracks.add(a.trackId); spaces.add(a.spaceId); }
  return {
    space: id => spaces.has(id),
    track: id => tracks.has(id),
    trackIsNew: id => id in d.tracks,
    row: id => !!d.activities[id]?.row,
    tabs: id => ({ updates: !!d.activities[id]?.updates, minutes: !!d.activities[id]?.minutes, files: !!d.activities[id]?.files }),
    kb: d.kb, research: d.research,
  };
}

/** The same list with that looked at (what the server will say after POST /seen). */
export function withSeen(n: NewSince, kind: SeenKind, id: string | null): NewSince {
  if (kind === 'kb' || kind === 'research') return { ...n, [kind]: false };
  if (kind === 'track') { const tracks = { ...n.tracks }; delete tracks[id!]; return { ...n, tracks }; }
  if (kind === 'space') {
    return {
      ...n,
      activities: Object.fromEntries(Object.entries(n.activities).filter(([, a]) => a.spaceId !== id)),
      tracks: Object.fromEntries(Object.entries(n.tracks).filter(([, t]) => t.spaceId !== id)),
    };
  }
  const a = n.activities[id!];
  if (!a) return n;
  const next = { ...a, [kind === 'activity' ? 'row' : kind]: false };
  const activities = { ...n.activities };
  if (next.row || next.updates || next.minutes || next.files) activities[id!] = next; else delete activities[id!];
  return { ...n, activities };
}

